/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
import {Dispatch} from 'redux';

import {getAllRoomsState} from 'services/room.service';
import store, {IAppStore} from 'store';
import {IRoom} from 'interfaces/roomProps';
import {IPortalError, transformToPortalError} from 'utility/error-handler';

export const ROOMS_PUBLISHING_STATE_FETCHING = 'ROOMS_PUBLISHING_STATE_FETCHING' as const;
export const ROOMS_PUBLISHING_STATE_DONE = 'ROOMS_PUBLISHING_STATE_DONE' as const;
export const ROOMS_PUBLISHING_STATE_ERROR = 'ROOMS_PUBLISHING_STATE_ERROR' as const;

export const allRoomsPublishingStateSelector = (
  {roomsPublishing}: IAppStore
): IAppStore['roomsPublishing']['publishingState'] =>
  roomsPublishing.publishingState;
export const roomPublishingStateSelector = (
  {roomsPublishing}: IAppStore,
  roomId: string
): number =>
  roomsPublishing.publishingState[roomId];

interface IRequestRoomsPublishingAction {
  type: typeof ROOMS_PUBLISHING_STATE_FETCHING;
  payload?: string[];
}
interface IReceiveRoomsPublishingAction {
  type: typeof ROOMS_PUBLISHING_STATE_DONE;
  payload: {roomId: string; isOnline: boolean; multipleStreams: boolean}[];
}
interface IFailedRoomsPublishingAction {
  type: typeof ROOMS_PUBLISHING_STATE_ERROR;
  error: null | IPortalError;
}
export type FetchingRoomsPublishingActions =
  | IRequestRoomsPublishingAction
  | IReceiveRoomsPublishingAction
  | IFailedRoomsPublishingAction;
interface IGetRoomsPublishingActions {
  request: (payload?: string[]) => IRequestRoomsPublishingAction;
  receive: (payload: {roomId: string; isOnline: boolean; multipleStreams: boolean}[]) => IReceiveRoomsPublishingAction;
  failed: (error: null | IPortalError) => IFailedRoomsPublishingAction;
}
export const getRoomsPublishingActions: IGetRoomsPublishingActions = {
  request: () => ({type: ROOMS_PUBLISHING_STATE_FETCHING}),
  receive: payload => ({
    type: ROOMS_PUBLISHING_STATE_DONE,
    payload
  }),
  failed: error => ({
    type: ROOMS_PUBLISHING_STATE_ERROR,
    error
  })
};

export const fetchRoomsPublishingState = (propRoomsIdOnDisplay?: string[]) => async(
  dispatch: Dispatch<FetchingRoomsPublishingActions>
): Promise<void> => {
  const state = store.getState();
  const roomList: IRoom[] = state.rooms?.roomList;
  const {receive, request, failed} = getRoomsPublishingActions;

  dispatch(request());

  const roomsIdOnDisplay: string[] = propRoomsIdOnDisplay || roomList.map(room => room.roomId);

  try {
    const response = await getAllRoomsState(roomsIdOnDisplay);

    dispatch(receive(response));
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

export type RoomsPublishingActions = FetchingRoomsPublishingActions;